import { Vector2 } from "three";
import { OnAnimationFrame } from "../animation/OnAnimationFrame";
import { ClassFlag } from "../dom-tools/ClassFlag";
import { ElementType } from "../dom-tools/ElementType";
import { Events, OnMouseDown, OnMouseMove, OnMouseUp, OnPointerLockChange } from "../dom-tools/EventListeners";
export class CustomMouse
{
  static readonly flag = new ClassFlag( "custom--mouse" );
  static readonly hoverFlag = new ClassFlag( "--hover" );

  static #x = 0;
  static get x(){ return CustomMouse.#x }

  static #y = 0;  
  static get y(){ return CustomMouse.#y }

  static #active = false;
  static get active()
  {
    return this.#active;
  }

  static #positionLocked = false;

  static readonly mouseCursorType = new ElementType<HTMLElement>( "custom--mouse--cursor" );
  static #mouseCursorElement:HTMLElement = null;
  static #hoveredElement:Element = null;
  static #onAnimationFrame = new OnAnimationFrame();
  static #mouseDragTarget:Element;

  static pageX( e:MouseEvent )
  {
    return CustomMouse.#active ? CustomMouse.#x : e.pageX;
  }

  static pageY( e:MouseEvent )
  {
    return CustomMouse.#active ? CustomMouse.#y : e.pageY;
  }

  static pagePosition( e:MouseEvent )
  {
    return CustomMouse.#active ? new Vector2( CustomMouse.#x, CustomMouse.#y ) : new Vector2( e.pageX, e.pageY ); 
  }

  static lock()
  {
    if ( ! CustomMouse.#active )
    {
      return;
    }

    CustomMouse.#positionLocked = true;    
  }

  static unlock()
  {
    if ( ! CustomMouse.#active )
    {
      return;
    }

    CustomMouse.#positionLocked = false;    
  }

  static async start( e:MouseEvent, unadjustedMovement:boolean = false )
  {    
    if ( this.#active )
    {
      return;
    }
    
    try 
    {
      CustomMouse.#x = e.pageX;      
      CustomMouse.#y = e.pageY;
      
      await ( document.body.requestPointerLock as any )( { unadjustedMovement: unadjustedMovement } );
      
      
      CustomMouse.#startTracking();
      

    }
    catch ( e )
    {
      console.error( e );
    }

  }   

  static #trackingCallback:(e:MouseEvent)=>void = null;

  static #addedListener:boolean = false;
  static #startTracking()
  {
    console.log( "start tracking" );
    this.#active = true;
    this.#positionLocked = false;
    
    CustomMouse.flag.set( document.body );

    if ( ! CustomMouse.#addedListener )
    {
      OnPointerLockChange.add( document,
        () =>
        {
          if ( document.pointerLockElement )
          {
            return;
          }
  
          CustomMouse.#stopTracking()
        }
      );

      

      CustomMouse.#onAnimationFrame.addListener(
        ()=>
        {
          let hoveredElement = document.elementFromPoint( CustomMouse.x, CustomMouse.y );

           
          if ( hoveredElement === CustomMouse.#hoveredElement  )
          {
            return;
          }

          //console.log( CustomMouse.x, ":", CustomMouse.y, "   ", CustomMouse.#hoveredElement, ">>", hoveredElement );

          if ( CustomMouse.#hoveredElement )
          {
            CustomMouse.hoverFlag.removeFrom( CustomMouse.#hoveredElement );
          }
          
          CustomMouse.#hoveredElement = hoveredElement;

          if ( CustomMouse.#hoveredElement )
          {
            CustomMouse.hoverFlag.set( CustomMouse.#hoveredElement );
          } 

          
        }
      );

      CustomMouse.#addedListener = true;
    }
    
    this.#onAnimationFrame.run();
    

    let cursorElement = CustomMouse.mouseCursorType.create();
    cursorElement = CustomMouse.mouseCursorType.create();

    let cursorStyle = cursorElement.style;
    cursorStyle.position = "fixed";
    cursorStyle.display  = "inline-block";
    cursorStyle.width    = "32px";
    cursorStyle.height   = "32px";
    cursorStyle.zIndex   = "100000"; 
    cursorStyle.pointerEvents = "none";

    let relativeCursorImagePath = "_media/general--graphics/mouse--cursor--default.png";

    let absoluteCursorURL = new URL( relativeCursorImagePath, window.location.origin );
    cursorStyle.backgroundImage = `url("${absoluteCursorURL}")`;

    document.body.appendChild( cursorElement );
    CustomMouse.#mouseCursorElement = cursorElement;

    CustomMouse.#trackingCallback = ( e:MouseEvent )=>
    { 
      if ( CustomMouse.#positionLocked )
      {
        return;
      }

      CustomMouse.#x += e.movementX;
      CustomMouse.#y += e.movementY;

      CustomMouse.#mouseCursorElement.style.left = CustomMouse.x + "px";
      CustomMouse.#mouseCursorElement.style.top  = CustomMouse.y + "px";
    }

    let relayMouseDown = (e:MouseEvent )=>
    {
      if ( ! CustomMouse.active || ! CustomMouse.#hoveredElement || ! e.isTrusted )
      {
        return;
      }

      CustomMouse.#mouseDragTarget = CustomMouse.#hoveredElement;

      let relayedEvent = Events.copyMouseEvent( e );
      relayedEvent.stopPropagation = ()=>{ e.stopPropagation(); }
      CustomMouse.#mouseDragTarget.dispatchEvent( relayedEvent );

      

    };

    let relayMouseUp = ( e:MouseEvent )=>
    {
      if ( ! CustomMouse.active || ! CustomMouse.#mouseDragTarget || ! e.isTrusted )
      {
        return;
      }

      let relayedEvent = Events.copyMouseEvent( e );
      relayedEvent.stopPropagation = ()=>{ e.stopPropagation(); }
      CustomMouse.#mouseDragTarget.dispatchEvent( relayedEvent );
    }

    OnMouseMove.add( document.body, CustomMouse.#trackingCallback );
    OnMouseDown.add( document.body, relayMouseDown );
    OnMouseUp.add( document.body, relayMouseUp );
    
  }

  static #stopTracking()
  {
    this.#onAnimationFrame.stop();
    CustomMouse.#active = false;
    CustomMouse.#positionLocked = false;

    if ( CustomMouse.#mouseCursorElement )
    {
      let cursorElement = CustomMouse.#mouseCursorElement;
      CustomMouse.#mouseCursorElement = null;
      document.body.removeChild( cursorElement );      
    }


    if ( ! CustomMouse.#trackingCallback )
    {
      return;
    }

    OnMouseMove.remove( document.body, CustomMouse.#trackingCallback );
  }

  static stop()
  {
    if ( ! this.#active )
    {
      return;
    }
    
    document.exitPointerLock();

    this.#stopTracking();
  }

}